import * as Sentry from '@sentry/react';

import { CorrespondenceApiError } from '@/lib/errors/correspondence-api-error';
import { FUNCTIONS_FETCH_ERROR_MESSAGE } from '@/lib/errors/transport-error-signature';
import { isInjectedExtractionScript } from '@/lib/sentry-injected-script-filter';
import { isDynamicImportFailure } from '@/lib/stale-chunk-reload';
import { isLocalSupabaseTarget } from '@/lib/supabase-target';
import {
  redactBreadcrumbDomains,
  redactDomainsDeep,
  redactDomainsFromText,
  redactExceptionValues,
} from '@/lib/sentry-domain-redaction';
import {
  redactSensitiveStringsDeep,
  redactSensitiveText,
  redactSensitiveUrl,
} from '@/lib/telemetry-redaction';

import type { BrowserOptions, ErrorEvent, Event, EventHint } from '@sentry/react';

type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];

function stringifyError(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error === undefined || error === null) {
    return '';
  }
  return JSON.stringify(error) ?? '';
}

const OBJECT_PLACEHOLDER = '[object Object]';
const CONSOLE_ARGUMENT_MESSAGE_KEYS = ['errorMessage', 'message', 'name'] as const;
const MAX_CONSOLE_ARGUMENT_LENGTH = 256;

function describeConsoleArgument(argument: unknown): string {
  if (argument === null || typeof argument !== 'object') {
    return stringifyError(argument);
  }
  const record = argument as Record<string, unknown>;
  for (const key of CONSOLE_ARGUMENT_MESSAGE_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  try {
    return stringifyError(argument).slice(0, MAX_CONSOLE_ARGUMENT_LENGTH);
  } catch {
    return OBJECT_PLACEHOLDER;
  }
}

/**
 * `captureConsoleIntegration` builds `event.message` by String()-joining the
 * console arguments, so any object argument collapses to "[object Object]" —
 * one unactionable Sentry issue absorbing unrelated errors. The SDK preserves
 * the original arguments on `extra.arguments`; rebuild the message from them,
 * preferring the error-ish string fields. Runs before the PII scrubbers, which redact the
 * rebuilt message like any other.
 */
function rebuildConsoleObjectMessage(event: ErrorEvent): void {
  if (event.logger !== 'console' || typeof event.message !== 'string') {
    return;
  }
  if (!event.message.includes(OBJECT_PLACEHOLDER)) {
    return;
  }
  const args = event.extra?.arguments;
  if (!Array.isArray(args) || args.length === 0) {
    return;
  }
  event.message = args.map(describeConsoleArgument).join(' ');
}

/**
 * Sentry `beforeSend` hook. Exported for unit testing.
 *
 * Returns `null` to drop the event, or a (possibly mutated) event to forward.
 * Drop rules:
 * - React Query `CancelledError`: expected during navigation/unmount.
 * - Transient network fetch failures (`Failed to fetch` / `Load failed` /
 *   `NetworkError when attempting to fetch`): browser-level, not actionable.
 * - PDF parsing errors: handled gracefully at call sites.
 * Mutation rule:
 * - `CorrespondenceApiError.details.rawBody` is scrubbed because non-facade
 *   upstreams can reflect request fragments (PII) into it.
 */
/** Backward-compatible export used by existing telemetry tests and call sites. */
export function redactPiiFromUrl(url: unknown): unknown {
  return redactSensitiveUrl(url);
}

const BREADCRUMB_URL_KEYS = ['url', 'to', 'from'] as const;

function redactBreadcrumbData(data: Record<string, unknown>): void {
  for (const key of BREADCRUMB_URL_KEYS) {
    if (key in data) {
      data[key] = redactPiiFromUrl(data[key]);
    }
  }
}

/** Console-captured breadcrumbs put stringified arguments into `crumb.message`. */
function redactBreadcrumbMessage(crumb: { message?: string }): void {
  if (typeof crumb.message !== 'string') {
    return;
  }
  crumb.message = redactSensitiveText(crumb.message);
}

function redactBreadcrumbs(breadcrumbs: ErrorEvent['breadcrumbs']): void {
  if (!Array.isArray(breadcrumbs)) {
    return;
  }
  for (const crumb of breadcrumbs) {
    if (crumb.data && typeof crumb.data === 'object') {
      redactBreadcrumbData(crumb.data as Record<string, unknown>);
    }
    redactBreadcrumbMessage(crumb);
  }
}

function redactTagUrls(tags: ErrorEvent['tags']): void {
  if (!tags) {
    return;
  }
  for (const [k, v] of Object.entries(tags)) {
    if (typeof v === 'string' && v.includes('?')) {
      tags[k] = redactPiiFromUrl(v) as string;
    }
  }
}

/** Every Sentry envelope shape these scrubbers run over. `Event` is the supertype
 * of `ErrorEvent`/`TransactionEvent` and is what the replay envelope arrives as -
 * see `redactUnhookedSentryEvent` for why that one reaches us by a different road. */
type SentryTelemetryEvent = ErrorEvent | TransactionEvent | Event;

function redactEventUrls(event: SentryTelemetryEvent): void {
  if (event.request?.url !== undefined) {
    event.request.url = redactPiiFromUrl(event.request.url) as string;
  }
  redactBreadcrumbs(event.breadcrumbs);
  redactTagUrls(event.tags);
}

function redactEventPii(event: SentryTelemetryEvent): void {
  if (typeof event.message === 'string') {
    event.message = redactDomainsFromText(event.message);
  }
  redactExceptionValues(event.exception?.values);
  redactBreadcrumbDomains(event.breadcrumbs);
  // Structured fields the free-text scrubbers above never reach. A raw
  // constituent domain landing in any of these (e.g. a future `setExtra`) is the
  // exact leak this backstop exists for, so scrub their string leaves too.
  //
  // Every result is REASSIGNED, never called for effect. `redactDomainsDeep`
  // scrubs in place only while the container is mutable; a frozen one is scrubbed
  // on a clone (`toWritableClone`) and returned, so discarding the return value
  // would throw the redacted copy away.
  //
  // Not currently reachable: every caller runs `redactEventDeep` first, whose
  // whole-event walk replaces a frozen child with an unfrozen clone via
  // `redactOwnDataProperty`, so these containers are already mutable by the time
  // this runs. Written this way so the guarantee belongs to this function rather
  // than to that call ordering - reorder the two and the effect-only form starts
  // shipping unredacted domains, silently.
  if (event.extra) {
    event.extra = redactDomainsDeep(event.extra) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = redactDomainsDeep(event.contexts) as typeof event.contexts;
  }
  if (event.request?.data !== undefined) {
    event.request.data = redactDomainsDeep(event.request.data);
  }
  if (event.user) {
    event.user = redactDomainsDeep(event.user) as typeof event.user;
  }
}

// Match expected/transient signatures against `event.message` too because
// captureConsoleIntegration can synthesize events without `originalException`.
const DROP_SUBSTRINGS = [
  // React Query cancellations during navigation/unmount.
  'CancelledError',
  'Query cancelled',
  // Browser-level fetch failures: `Failed to fetch` (Chrome/Firefox),
  // `Load failed` (Safari), `NetworkError when attempting to fetch`
  // (older Firefox). Not actionable as application errors.
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch',
  // PDF parsing errors handled gracefully at call sites.
  'Invalid PDF structure',
  'InvalidPDFException',
  // supabase-js `FunctionsFetchError` message: the request never produced an
  // HTTP response (aborted refetch on navigation, network blip, cold start).
  // Call sites gate this with `isTransientFetchError` (which shares this exact
  // constant), but this backstop drops the whole class app-wide for any
  // unguarded/future site. Trade-off: a genuine edge-function outage goes quiet
  // here too — accepted, because a real outage trips every other query's alarms
  // and this is not where you'd first notice it.
  FUNCTIONS_FETCH_ERROR_MESSAGE,
  // Fallback for the auth refresh-expiry class (primary gate is
  // EXPECTED_AUTH_REFRESH_CODES below). The GoTrue server formats these as
  // `Invalid Refresh Token: <reason>`; matching the prose catches the case
  // where the event carries only `event.message` (no `originalException.code`
  // to gate on). Colon-prefixed so actionable auth errors still forward.
  'Invalid Refresh Token:',
  // Kong's upstream-DNS failure body (`{"message":"name resolution failed"}`)
  // when the gateway cannot reach a service container — locally, a
  // partially-started stack; on hosted Supabase, a platform outage that trips
  // every other alarm first. Same trade-off as FUNCTIONS_FETCH_ERROR_MESSAGE.
  'name resolution failed',
];

/**
 * Supabase auth-js `console.error`s the raw `AuthApiError` when a persisted
 * session's refresh token is expired/revoked (GoTrueClient `_recoverAndRefresh`),
 * then emits SIGNED_OUT — which AuthProvider already handles (clear caches +
 * "Session expired" toast + re-auth). These error CODES are a version-checked
 * auth-js client contract (`error-codes.ts`), unlike the GoTrue server message
 * prose — gating on the code keeps the filter from silently going inert if the
 * server wording changes, and from over-dropping a future security-relevant
 * message that happens to share the `Invalid Refresh Token:` prefix.
 */
const EXPECTED_AUTH_REFRESH_CODES: ReadonlySet<string> = new Set([
  'refresh_token_not_found',
  'refresh_token_already_used',
  'session_expired',
]);

/** `sdkProcessingMetadata` holds SDK-internal state whose nested values are live
 * objects shared across the trace — the captured span scopes (whose Client owns
 * the transport promise buffer with a getter-only `$`), and the
 * `dynamicSamplingContext`, which on transactions is the very object frozen onto
 * the root span (`getDynamicSamplingContextFromSpan` returns the cached
 * `_frozenDsc`). Walking any of it in place either throws or corrupts state
 * every later event in the trace reuses — the crash class behind
 * https://public-pulse-fq.sentry.io/issues/PUBLIC-PULSE-APP-BK. So the whole
 * field is detached before the walk and the per-event wrapper reattached after
 * (the SDK reads `spanCountBeforeProcessing` from it post-hook for dropped-span
 * accounting). The one shipping subfield — `dynamicSamplingContext`, sent as the
 * envelope trace header — is redacted via a clone so a non-url-sourced
 * transaction name is scrubbed without mutating the span-owned original. */
function redactEventDeep(event: SentryTelemetryEvent): void {
  const target = event as SentryTelemetryEvent & {
    sdkProcessingMetadata?: Record<string, unknown>;
  };
  const metadata = target.sdkProcessingMetadata;
  delete target.sdkProcessingMetadata;
  try {
    redactSensitiveStringsDeep(target);
  } finally {
    if (metadata !== undefined) {
      target.sdkProcessingMetadata = metadata;
      redactShippingDsc(metadata);
    }
  }
}

/** The DSC ships as the envelope trace header, so its string values must be
 * scrubbed — but the object is shared with the root span, so redact a shallow
 * clone and swap it onto the (per-event, safe-to-mutate) metadata wrapper rather
 * than rewriting the live original. DSC values are flat strings; a shallow clone
 * is sufficient. */
function redactShippingDsc(metadata: Record<string, unknown>): void {
  const dsc = metadata.dynamicSamplingContext;
  if (dsc === null || typeof dsc !== 'object' || Array.isArray(dsc)) {
    return;
  }
  const clone: Record<string, unknown> = { ...(dsc as Record<string, unknown>) };
  redactSensitiveStringsDeep(clone);
  redactDomainsDeep(clone);
  metadata.dynamicSamplingContext = clone;
}

/**
 * Realtime channel-failure warnings against a Supabase on the developer's own
 * machine describe the local docker stack (container down, `pp start`
 * incomplete), never the product. Against a hosted instance the same warning
 * is a genuine infrastructure signal and must keep flowing. Matches only the
 * persistent-failure signature — the sibling `Channel authorization denied,
 * parking` capture is an RLS verdict that reproduces identically on hosted
 * targets, so it stays reportable even from a local stack.
 */
function isLocalRealtimeInfraNoise(event: ErrorEvent): boolean {
  return (
    typeof event.message === 'string' &&
    event.message.startsWith('[Real-time] Persistent channel failure') &&
    isLocalSupabaseTarget()
  );
}

/**
 * auth-js wraps any network-level fetch failure (offline, DNS, cold start) in
 * AuthRetryableFetchError. Gate on the class name (a stable auth-js export)
 * rather than the message, which is often the empty serialization `{}`. Only
 * the token auto-refresh loop retries this class — one-shot calls (e.g. the
 * AAL lookup) surface it directly, and against hosted GoTrue a 5xx here can
 * be an outage blocking logins — so the drop applies to local targets only.
 */
const isLocalRetryableAuthFetchNoise = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AuthRetryableFetchError' && isLocalSupabaseTarget();

export function filterSentryEvent(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  if (isInjectedExtractionScript(event) || isLocalRealtimeInfraNoise(event)) {
    return null;
  }

  // Before the drop rules: a transient signature hidden inside an object
  // argument (e.g. `{message: 'Failed to fetch'}`) must land in the haystack.
  rebuildConsoleObjectMessage(event);

  const error: unknown = hint.originalException;
  // `error` is `any` from the SDK types; stringify defensively without
  // invoking Object's default `[object Object]` fallback.
  const errorMessage = error instanceof Error ? error.message : '';
  const errorString = errorMessage || stringifyError(error);
  const haystack = `${errorMessage} ${errorString} ${event.message ?? ''}`;

  // Primary gate for the auth refresh-expiry class: the AuthApiError `code` is a
  // stable client contract, the message prose is not. See EXPECTED_AUTH_REFRESH_CODES.
  const authCode = (error as { code?: unknown } | null | undefined)?.code;
  if (typeof authCode === 'string' && EXPECTED_AUTH_REFRESH_CODES.has(authCode)) {
    return null;
  }

  if (isLocalRetryableAuthFetchNoise(error)) {
    return null;
  }

  // The stale-chunk class is exempted from the drop list, not merely ordered
  // ahead of it: its canonical message is `Failed to fetch dynamically imported
  // module`, which the generic `Failed to fetch` transient-network entry would
  // otherwise swallow whole. Sharing `isDynamicImportFailure` with the recovery
  // path keeps the exemption and the recovery matching the same signatures — if
  // one grows a signature the other must not silently keep dropping it.
  if (!isDynamicImportFailure(haystack) && DROP_SUBSTRINGS.some((sig) => haystack.includes(sig))) {
    return null;
  }
  // PDF parsing variants where the message mentions both words separately.
  if (haystack.includes('PDF') && haystack.includes('parsing')) {
    return null;
  }

  // Scrub authentication material from every string leaf before the narrower
  // constituent-domain pass. This covers exception values, span data, console
  // breadcrumbs, and any future SDK field that can carry a callback URL.
  redactEventDeep(event);

  // Strip constituent PII from URLs (request, breadcrumbs, tags) before forwarding.
  redactEventUrls(event);

  // Scrub `CorrespondenceApiError.details.rawBody`. The field is an opt-in
  // ops debugging hatch, but the backend's `AdapterErrorDetails` whitelist
  // only covers facade errors — non-facade upstreams (CDN 5xx, WAF, Vault,
  // Deno runtime) can reflect request fragments (organizationId, recipient
  // emails, subject lines, body content) into `rawBody`. Runs BEFORE the PII
  // scrub below so `safeDetails` is also walked for stray domains/emails.
  if (error instanceof CorrespondenceApiError && error.details) {
    const { rawBody: _rawBody, ...safeDetails } = error.details;
    redactSensitiveStringsDeep(safeDetails);
    event.contexts = {
      ...event.contexts,
      error: {
        ...event.contexts?.error,
        details: safeDetails,
      },
    };
  }

  // Scrub email addresses and bare domains from free-text AND structured fields
  // (message, exception values, breadcrumb messages, extra, contexts,
  // request.data, user). Runs after URL redaction so the structured param scrub
  // completes against intact hosts first, and after the rawBody handling so the
  // surviving `safeDetails` context is covered too.
  redactEventPii(event);

  return event;
}

/** Sentry transactions bypass `beforeSend`, so apply the same URL/auth boundary. */
export function filterSentryTransaction(event: TransactionEvent): TransactionEvent | null {
  redactEventDeep(event);
  redactEventUrls(event);
  redactEventPii(event);
  return event;
}

/** `beforeSend` only ever sees error events and `beforeSendTransaction` only
 * transactions; every other envelope type ships without passing either hook. */
function hasBeforeSendHook(type: Event['type']): boolean {
  return type === undefined || type === 'transaction';
}

/**
 * Redacts the envelope types no `beforeSend*` hook can reach. Registered as a
 * global event processor, which is the ONLY hook that runs for them.
 *
 * Session Replay is the live case and it ships the raw address bar: the replay
 * envelope carries a `urls` array built from
 * `location.pathname + hash + search` at capture start, plus one entry per SPA
 * navigation, so a `?q=<constituent name>` lands there verbatim. That envelope is
 * assembled by the standalone `prepareEvent` rather than the client's
 * `_prepareEvent` - the replay SDK does this deliberately - and `beforeSend`
 * lives only in `_prepareEvent`. `prepareEvent` does run scope event processors,
 * hence this hook. `beforeAddRecordingEvent` is not a substitute: it sees rrweb
 * recording frames, never the `replay_event` payload.
 *
 * Returns the event unchanged for error events and transactions so the work is
 * not duplicated - `filterSentryEvent` / `filterSentryTransaction` own those.
 */
export function redactUnhookedSentryEvent(event: Event): Event {
  if (hasBeforeSendHook(event.type)) {
    return event;
  }
  redactEventDeep(event);
  redactEventPii(event);
  redactReplayUrls(event);
  return event;
}

/** The replay envelope's `urls` array is the address bar verbatim, and it is the
 * one shipping field the email/domain pass cannot otherwise reach: it is neither
 * free text nor one of the structured fields `redactEventPii` walks, so an email
 * in a path segment (or in a param absent from the sensitive-key allow-list)
 * survives the auth pass. Host and constituent-shaped segments are replaced;
 * path structure and opaque ids are not, so the url stays diagnosable. */
function redactReplayUrls(event: Event): void {
  const target = event as { urls?: unknown };
  if (Array.isArray(target.urls)) {
    target.urls = redactDomainsDeep(target.urls);
  }
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 *
 * Environment variables required:
 * - VITE_SENTRY_DSN: Sentry Data Source Name (required for Sentry to work)
 * - VITE_SENTRY_ENVIRONMENT: Environment name (e.g., 'production', 'staging', 'development')
 * - VITE_SENTRY_TRACES_SAMPLE_RATE: Performance monitoring sample rate (0.0 to 1.0)
 * - VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: Session replay sample rate (0.0 to 1.0)
 * - VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: Error replay sample rate (0.0 to 1.0)
 */
export function initSentry() {
  if (import.meta.env.DEV) {
    return;
  }

  const env = import.meta.env as Record<string, string | undefined>;
  const dsn = env.VITE_SENTRY_DSN;

  // Skip initialization if DSN is not provided
  if (!dsn) {
    // `console.warn` rather than `console.debug` per the project's
    // `no-console` rule — this signal is worth surfacing once at boot.
    console.warn('Sentry DSN not configured. Skipping Sentry initialization.');
    return;
  }

  const environment = env.VITE_SENTRY_ENVIRONMENT || 'development';
  const tracesSampleRate = Number.parseFloat(env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1');
  const replaysSessionSampleRate = Number.parseFloat(
    env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1',
  );
  const replaysOnErrorSampleRate = Number.parseFloat(
    env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0',
  );

  const options: BrowserOptions = {
    dsn,
    environment,
    // `app.shell` stays set because alert filters key on it.
    initialScope: { tags: { 'app.shell': 'web' } },

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        // Replay records network requests separately from `event.breadcrumbs`
        // and ships them outside the `beforeSend` pipeline. Hook the network
        // sanitiser so the same PII params get scrubbed from the replay envelope
        // (TR-2 coverage; otherwise sampled replays leak constituent names
        // typed mid-search before promotion fires).
        networkRequestHeaders: [],
        networkResponseHeaders: [],
        beforeAddRecordingEvent: (event) => {
          redactSensitiveStringsDeep(event);
          return event;
        },
      }),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.publicpulse\.ai/],

    // Capture Replay for 10% of all sessions,
    // plus 100% of sessions with an error
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,

    beforeSend: filterSentryEvent,
    beforeSendTransaction: filterSentryTransaction,
    beforeBreadcrumb: (breadcrumb) => {
      redactSensitiveStringsDeep(breadcrumb);
      return breadcrumb;
    },
  };

  Sentry.init(options);

  // Closes the replay envelope, which no `beforeSend*` hook can see - see
  // `redactUnhookedSentryEvent`. MUST NOT be folded into `beforeSend`.
  Sentry.addEventProcessor(redactUnhookedSentryEvent);
}
